import React, { useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { MUTATION_TYPE, useMissionRoles, useToast } from "hooks";
import { ADD_MISSION_ROLE, UPDATE_MISSION_ROLE, SUBMIT_AI_ROLE_BUILDER_JOB } from "graphql/mutations";
import { GET_MISSION_ROLES } from "graphql/queries";
import { prepareAIRoleData } from "utils";
import {
  BLUEPRINT_TAB,
  CLEARED_JOB_DESCRIPTION_UPLOAD_VALUES,
  JOB_DESCRIPTION_TAB,
  ROLE_BUILDER_STEP,
  TALENT_SIMPLE_ARCHETYPES_OPTIONS,
  getRoleBuilderStep,
  isDraftInProgress,
} from "constants/index";
import { useRoleBuilderSubmit, useRoleBuilderTask, useRoleBuilderVetting } from "hooks";

/**
 * useRoleBuilder
 *
 * The Talent Simple role builder's step machine: which step and tab are showing, the draft
 * being built, and the form props each step saves through.
 *
 * @param {Object}    task         An AI role builder task to review, when resuming
 * @param {Object}    role         An unfinished draft being resumed from the roles list
 * @param {String}    missionId    Mission the role is created under
 * @param {String}    companyName  Company the role is for, from the organization
 * @param {String}    companyUrl   Company site, from the organization
 * @param {Function}  onClose      Called when the builder should close
 */
export default function useRoleBuilder({ task, role, missionId, companyName, companyUrl, onClose } = {}) {
  const initialStep = role?.builderStep || ROLE_BUILDER_STEP.JOB_DESCRIPTION;
  const [activeStep, setActiveStep] = useState(initialStep);
  const [selectedTab, setSelectedTab] = useState(getInitialTab(initialStep, task || role));
  const [draftRole, setDraftRole] = useState(role);
  const continuingLater = useRef(false);
  const { toast } = useToast();
  const { buildFormData } = useMissionRoles({
    skipQuery: true,
    missionId,
    archetypeOptions: TALENT_SIMPLE_ARCHETYPES_OPTIONS,
  });

  const step = getRoleBuilderStep(activeStep);
  const nextStep = getRoleBuilderStep(activeStep, 1)?.key;
  const previousStep = getRoleBuilderStep(activeStep, -1)?.key;
  const isJobDescription = activeStep === ROLE_BUILDER_STEP.JOB_DESCRIPTION;
  const isBlueprint = activeStep === ROLE_BUILDER_STEP.BLUEPRINT;
  const isReviewStep = activeStep === ROLE_BUILDER_STEP.REVIEW;
  const hasDraft = !!draftRole?.id;
  const isSubmitted = hasDraft && !isDraftInProgress(draftRole.state);

  const { taskId, reviewTask, isTaskReady, isReviewReady, handleJobSubmitted, discardReviewedTask } =
    useRoleBuilderTask({ task });
  const {
    vettingRole,
    vettingLoading,
    refetchVetting,
    blueprintId,
    applyingBlueprint,
    selectBlueprint: applyBlueprint,
    hasInterviewQuestions,
  } = useRoleBuilderVetting({ draftRole, isBlueprint, isReviewStep });
  const { submitRole, submitting } = useRoleBuilderSubmit({ draftRole, onClose });

  // Once the job description has been sent, the upload tab only shows what was sent.
  const isJobDescriptionSent = isJobDescription && (hasDraft || !!taskId);
  const isReviewingJobDescription =
    isJobDescription && (isJobDescriptionSent || selectedTab === JOB_DESCRIPTION_TAB.review);
  const awaitingTask = isJobDescription && !hasDraft && !!taskId && !isReviewReady ? reviewTask : null;

  const moveTo = (stepKey) => {
    setActiveStep(stepKey);
    setSelectedTab(getInitialTab(stepKey, hasDraft || !!taskId));
  };

  const selectBlueprint = async (value) => {
    const result = await applyBlueprint(value);

    if (result?.data) setSelectedTab(BLUEPRINT_TAB.customize);
  };

  const handleJobDescriptionSubmitted = (result) => {
    setSelectedTab(JOB_DESCRIPTION_TAB.review);
    handleJobSubmitted(result);
  };

  const advanceOrClose = (missionRole) => {
    if (missionRole) setDraftRole(missionRole);

    if (continuingLater.current) {
      continuingLater.current = false;
      onClose();

      return;
    }

    moveTo(nextStep);
  };

  const goBack = () => {
    if (previousStep) moveTo(previousStep);
  };

  const continueLater = (submitPartial) => {
    if (awaitingTask || isReviewStep) return onClose();

    continuingLater.current = true;
    submitPartial({ builderStep: activeStep, isInProgress: true });
  };

  const handleError = () => (continuingLater.current = false);

  const handleRoleCreated = (result) => {
    const missionRole = result?.addMissionRole?.missionRole;

    toast.success(<FormattedMessage id="Missions.RoleAddButton.CreateSuccess" />);
    discardReviewedTask(missionRole?.id);
    advanceOrClose(missionRole);
  };

  const handleRoleUpdated = (result) => {
    toast.success(<FormattedMessage id="Missions.RoleAddButton.EditSuccess" />);
    advanceOrClose(result?.updateMissionRole?.missionRole);
  };

  const getReviewValues = () =>
    buildFormData(prepareAIRoleData(reviewTask?.results) || {}, false, true, ROLE_BUILDER_STEP.INFORMATION);

  // Form only reads initialValues when it mounts, so anything that changes with the step goes through values.
  // The generated values are keyed on the request rather than its polled copy, so a poll never overwrites edits.
  const stepValues = useMemo(() => {
    if (hasDraft) return nextStep && { isInProgress: !isSubmitted, builderStep: nextStep };
    if (isTaskReady) return { ...CLEARED_JOB_DESCRIPTION_UPLOAD_VALUES, ...getReviewValues() };

    return null;
  }, [hasDraft, isSubmitted, nextStep, isTaskReady, taskId]);

  const roleMutationProps = (mutationType, mutationName) => ({
    parentKeyName: "missionId",
    parentKeyValue: missionId,
    mutationType,
    mutationData: {
      mutationName: { [mutationType]: mutationName },
      refetchQueries: [GET_MISSION_ROLES],
      refetchAfterMutate: true,
    },
  });

  const getFormProps = () => {
    if (hasDraft) {
      const updateProps = {
        initialValues: buildFormData(draftRole, false, true),
        values: stepValues,
        updateId: draftRole.id,
        ...roleMutationProps(MUTATION_TYPE.update, UPDATE_MISSION_ROLE),
      };

      // Review publishes instead of saving, but keeps the mutation props so useMutateData keeps its hooks.
      return isReviewStep
        ? { ...updateProps, onSubmit: isSubmitted ? onClose : () => submitRole() }
        : { ...updateProps, onCompleted: handleRoleUpdated };
    }

    if (isReviewingJobDescription) {
      return {
        initialValues: getReviewValues(),
        values: stepValues,
        ...roleMutationProps(MUTATION_TYPE.add, ADD_MISSION_ROLE),
        onCompleted: handleRoleCreated,
      };
    }

    return {
      initialValues: { companyName: companyName || "", companyUrl: companyUrl || "", missionId },
      mutationType: MUTATION_TYPE.add,
      mutationData: {
        mutationName: { add: SUBMIT_AI_ROLE_BUILDER_JOB },
        refetchAfterMutate: false,
      },
      onCompleted: handleJobDescriptionSubmitted,
    };
  };

  return {
    activeStep,
    companyName,
    companyUrl,
    summaryRole: vettingRole || draftRole,
    stepDescriptionId: step.descriptionId,
    tabTitleIds: step.tabTitleIds,
    selectedTab,
    setSelectedTab,
    isJobDescriptionSent,
    isReviewStep,
    isSubmitted,
    isCustomizing: isBlueprint && selectedTab === BLUEPRINT_TAB.customize,
    awaitingTask,
    vettingRole,
    vettingLoading,
    refetchVetting,
    blueprintId,
    applyingBlueprint,
    selectBlueprint,
    canGoBack: !!previousStep,
    goBack,
    canContinueLater: !isSubmitted && (hasDraft || !!taskId),
    continueLater,
    handleError,
    canSubmit: !isBlueprint || hasInterviewQuestions,
    submitting,
    formProps: getFormProps(),
  };
}

const getInitialTab = (stepKey, wasSent) =>
  stepKey === ROLE_BUILDER_STEP.JOB_DESCRIPTION && wasSent ? JOB_DESCRIPTION_TAB.review : 0;
